"use client";

import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const parentCategoryPillScrollHide =
  "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:h-0 [&::-webkit-scrollbar]:w-0";

function useChevronHorizontalScroll(itemCount: number) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const syncScrollEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const overflowing = scrollWidth > clientWidth + 2;
    setHasOverflow(overflowing);
    if (!overflowing) {
      setCanLeft(false);
      setCanRight(false);
      return;
    }
    setCanLeft(scrollLeft > 2);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    syncScrollEdges();
    const id = window.setTimeout(syncScrollEdges, 100);
    el.addEventListener("scroll", syncScrollEdges, { passive: true });
    const ro = new ResizeObserver(syncScrollEdges);
    ro.observe(el);
    return () => {
      window.clearTimeout(id);
      el.removeEventListener("scroll", syncScrollEdges);
      ro.disconnect();
    };
  }, [syncScrollEdges, itemCount]);

  const scrollBy = useCallback((delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
    window.setTimeout(syncScrollEdges, 350);
  }, [syncScrollEdges]);

  return { scrollRef, hasOverflow, canLeft, canRight, scrollBy };
}

const chevronBtnClass =
  "h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-40";

/** Full-width chip row: hidden scrollbar + chevrons (command chat, prompt library sheet). */
export function HorizontalChevronChipRow({
  itemCount,
  className,
  trackClassName,
  children,
}: {
  itemCount: number;
  className?: string;
  /** Extra classes on the scrollable track (e.g. min-h). */
  trackClassName?: string;
  children: ReactNode;
}) {
  const { scrollRef, hasOverflow, canLeft, canRight, scrollBy } =
    useChevronHorizontalScroll(itemCount);

  return (
    <div
      className={cn(
        "flex min-w-0 w-full max-w-full items-center gap-1.5",
        className
      )}
    >
      {hasOverflow ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            chevronBtnClass,
            "hidden sm:inline-flex rounded-full border-border bg-background shadow-sm"
          )}
          disabled={!canLeft}
          aria-label="Scroll categories left"
          onClick={() => scrollBy(-200)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      ) : null}
      <div
        ref={scrollRef}
        className={cn(
          "min-h-8 min-w-0 flex-1 py-0.5",
          hasOverflow ? "overflow-x-auto scroll-smooth" : "overflow-x-hidden",
          parentCategoryPillScrollHide,
          trackClassName
        )}
      >
        {children}
      </div>
      {hasOverflow ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            chevronBtnClass,
            "hidden sm:inline-flex rounded-full border-border bg-background shadow-sm"
          )}
          disabled={!canRight}
          aria-label="Scroll categories right"
          onClick={() => scrollBy(200)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}

export function ParentCategoryPillScroller({
  itemCount,
  maxWidthClassName,
  children,
}: {
  itemCount: number;
  maxWidthClassName?: string;
  children: ReactNode;
}) {
  const { scrollRef, hasOverflow, canLeft, canRight, scrollBy } =
    useChevronHorizontalScroll(itemCount);

  const middleMax =
    maxWidthClassName ??
    "max-w-[200px] sm:max-w-[240px] md:max-w-[260px] lg:max-w-[280px]";

  return (
    <div className="flex h-9 max-w-full shrink-0 items-stretch overflow-hidden rounded-md border border-input bg-background shadow-sm">
      {hasOverflow ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-auto w-8 shrink-0 rounded-none border-r px-0 text-muted-foreground hover:text-foreground disabled:opacity-40"
          disabled={!canLeft}
          aria-label="Scroll left"
          onClick={() => scrollBy(-200)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      ) : null}
      <div
        ref={scrollRef}
        className={cn(
          "min-h-9 min-w-0",
          hasOverflow ? "overflow-x-auto scroll-smooth" : "overflow-x-hidden",
          middleMax,
          parentCategoryPillScrollHide
        )}
      >
        {children}
      </div>
      {hasOverflow ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-auto w-8 shrink-0 rounded-none border-l px-0 text-muted-foreground hover:text-foreground disabled:opacity-40"
          disabled={!canRight}
          aria-label="Scroll right"
          onClick={() => scrollBy(200)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}
